import React from 'react'
import { StaticQuery, graphql } from 'gatsby'

// Language
import { getLanguage } from 'services/language'
// Libraries
import { find as _find } from 'lodash'

// Third Party
import { Helmet } from 'react-helmet'

import favicon from '../img/favicon.png'

const query = graphql`
  {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        defaultKeywords: keywords
      }
    }
    file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fluid(maxWidth: 100) {
          ...GatsbyImageSharpFluid
        }
        resize(width: 200, quality: 100) {
          src
        }
      }
    }
  }
`

const ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  ndash: '–',
  mdash: '—',
}

// WordPress returns titles/descriptions with HTML entities (e.g. "&amp;"),
// React would escape them again, so decode them first.
const decodeEntities = (text = '') =>
  text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity) => {
    if (entity[0] === '#') {
      const code =
        entity[1].toLowerCase() === 'x'
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10)
      return String.fromCodePoint(code)
    }
    return ENTITIES[entity.toLowerCase()] || match
  })

const stripHtml = (html) =>
  typeof html === 'string'
    ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    : ''

function SEO({
  title,
  description,
  image,
  pathname,
  article,
  keywords,
  yoast,
}) {
  const language = getLanguage()

  return (
    <StaticQuery
      query={query}
      render={({
        site: {
          siteMetadata: {
            defaultKeywords,
            defaultTitle,
            defaultDescription,
            siteUrl,
          },
        },
        file: featuredImage,
      }) => {
        const yoastMeta = (yoast && yoast.meta) || []
        const getYoastMeta = (key, value) => {
          const meta = _find(yoastMeta, (item) => item && item[key] === value)
          return meta && typeof meta.content === 'string' && meta.content
            ? meta.content
            : null
        }

        // Yoast values always win, the passed props are only a fallback
        title =
          title || getYoastMeta('property', 'og:title') || defaultTitle
        description =
          getYoastMeta('name', 'description') ||
          getYoastMeta('property', 'og:description') ||
          stripHtml(description) ||
          defaultDescription

        const seo = {
          title: decodeEntities(title),
          description: decodeEntities(description),
          image: image
            ? `${siteUrl}${image}`
            : `${siteUrl}${featuredImage.childImageSharp.resize.src}`,
          url: `${siteUrl}${pathname || '/'}`,
          keywords: keywords || defaultKeywords,
        }

        return (
          <Helmet title={seo.title}>
            <html lang="nl" />
            <link rel="icon" href={favicon} />
            <meta name="description" content={seo.description} />
            <meta name="keywords" content={seo.keywords} />
            <meta name="image" content={seo.image} />
            <meta name="og:site_name" content={defaultTitle} />
            {seo.url && <meta property="og:url" content={seo.url} />}
            {(article ? true : null) && (
              <meta property="og:type" content="article" />
            )}
            {seo.title && <meta property="og:title" content={seo.title} />}
            {seo.description && (
              <meta property="og:description" content={seo.description} />
            )}
            {seo.image && (
              <meta property="og:image" itemProp="image" content={seo.image} />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            {seo.title && <meta name="twitter:title" content={seo.title} />}
            {seo.description && (
              <meta name="twitter:description" content={seo.description} />
            )}
            {seo.image && <meta name="twitter:image" content={seo.image} />}
            {language === 'nl_NL'
              ? [
                  <meta key={0} property="og:locale" content="nl_NL" />,
                  <meta
                    key={1}
                    property="og:locale:alternate"
                    content="en_GB"
                  />,
                ]
              : [
                  <meta key={0} property="og:locale" content="en_GB" />,
                  <meta
                    key={1}
                    property="og:locale:alternate"
                    content="nl_NL"
                  />,
                ]}
            <script 
              id="Cookiebot"
              src="https://consent.cookiebot.com/uc.js"
              data-cbid="4ffc73a0-49dc-4d81-add3-63ef8f723a27"
              data-blockingmode="auto"
              type="text/javascript"
            />
            <script
              defer
              src="//code.tidio.co/ihhasqbeiovnab3qumjeuyq1l5iapsns.js"
            />
            <meta
              name="google-site-verification"
              content="u-Kl7EaXQKlCheg3j-ajbTup5A4L5LttIyycMS3f1aU"
            />

            
          </Helmet>
        )
      }}
    />
  )
}

export default SEO
